/**
 * Connection Types Configuration
 *
 * Central registry for all supported connection types.
 * Add new connection types here - no other code changes needed!
 */

// Icon components are passed as strings that map to Lucide icon names
// The rendering component should import and render the appropriate Lucide icon
export const CONNECTION_TYPES = {
  database: {
    id: 'database',
    name: 'Database',
    icon: 'Database',
    color: '#059669',
    description: 'Connect to SQL databases (PostgreSQL, MySQL, Oracle, SQL Server)',
    fields: [
      {
        key: 'type',
        label: 'Database Type',
        type: 'select',
        options: [
          { value: 'postgresql', label: 'PostgreSQL' },
          { value: 'mysql', label: 'MySQL' },
          { value: 'mariadb', label: 'MariaDB' },
          { value: 'oracle', label: 'Oracle' },
          { value: 'sqlserver', label: 'SQL Server' }
        ],
        required: true
      },
      { key: 'host', label: 'Host', type: 'text', required: true, placeholder: 'localhost' },
      { key: 'port', label: 'Port', type: 'number', required: true, placeholder: '5432' },
      { key: 'name', label: 'Database Name', type: 'text', required: true, placeholder: 'my_database' },
      { key: 'user', label: 'Username', type: 'text', required: true, placeholder: 'postgres' },
      { key: 'password', label: 'Password', type: 'password', required: false, placeholder: '••••••••' },
      { key: 'schema', label: 'Schema', type: 'text', required: false, placeholder: 'public', help: 'Optional: Database schema name' },
      {
        key: 'sslmode',
        label: 'SSL Mode',
        type: 'select',
        options: [
          { value: '', label: 'Default' },
          { value: 'disable', label: 'Disable' },
          { value: 'allow', label: 'Allow' },
          { value: 'prefer', label: 'Prefer' },
          { value: 'require', label: 'Require' },
          { value: 'verify-ca', label: 'Verify CA' },
          { value: 'verify-full', label: 'Verify Full' }
        ],
        required: false,
        help: 'SSL connection mode (use "require" for cloud databases like Neon, Supabase, etc.)'
      },
      {
        key: 'disable_read_only',
        label: 'Disable Read-Only Mode',
        type: 'checkbox',
        required: false,
        help: 'Enable for connection poolers (Neon, PgBouncer) that don\'t support read-only transactions'
      }
    ],
    supportsSSL: true // Special flag for SSL certificate upload
  },
  odoo: {
    id: 'odoo',
    name: 'Odoo',
    icon: 'Link',
    color: '#7c3aed',
    description: 'Connect to your Odoo instance to access business data and workflows',
    fields: [
      { key: 'url', label: 'Odoo URL', type: 'text', required: true, placeholder: 'https://yourcompany.odoo.com', help: 'Your Odoo instance URL' },
      { key: 'database', label: 'Database Name', type: 'text', required: true, placeholder: 'your_odoo_database', help: 'Your Odoo database name' },
      { key: 'username', label: 'Username', type: 'text', required: true, placeholder: 'admin' },
      { key: 'password', label: 'Password', type: 'password', required: false, placeholder: '••••••••' },
      { key: 'api_key', label: 'API Key', type: 'password', required: false, placeholder: 'Your Odoo API Key', help: 'Alternative to password. Generate in Odoo: Settings → Users → API Keys' }
    ]
  },
  jira: {
    id: 'jira',
    name: 'Jira',
    icon: 'ClipboardList',
    color: '#2563eb',
    description: 'Connect to your Jira instance to access issues and project data',
    fields: [
      { key: 'url', label: 'Jira URL', type: 'text', required: true, placeholder: 'https://yourcompany.atlassian.net', help: 'Your Jira instance URL' },
      { key: 'email', label: 'Email', type: 'email', required: true, placeholder: 'user@company.com' },
      { key: 'api_token', label: 'API Token', type: 'password', required: true, placeholder: 'Your Jira API Token', help: 'Generate in Jira: Account Settings → Security → API Tokens' }
    ]
  },
  asana: {
    id: 'asana',
    name: 'Asana',
    icon: 'CheckSquare',
    color: '#f06a6a',
    description: 'Connect to Asana to access tasks and project management data',
    fields: [
      { key: 'workspace_gid', label: 'Workspace GID', type: 'text', required: true, placeholder: '1234567890', help: 'Find your workspace GID in Asana settings' },
      { key: 'personal_access_token', label: 'Personal Access Token', type: 'password', required: true, placeholder: 'Your Asana PAT', help: 'Generate in Asana: My Profile Settings → Apps → Manage Developer Apps → Create new token' }
    ]
  },
  zoho_crm: {
    id: 'zoho_crm',
    name: 'Zoho CRM',
    icon: 'BarChart2',
    color: '#e42527',
    description: 'Connect to Zoho CRM to access customer relationship management data',
    isOAuth: true,
    oauthProvider: 'zoho_crm',
    fields: [
      { key: 'oauth_client_id', label: 'Client ID', type: 'text', required: true, isOAuthCredential: true,
        placeholder: 'From Zoho API Console', help: 'Create at api-console.zoho.com' },
      { key: 'oauth_client_secret', label: 'Client Secret', type: 'password', required: true, isOAuthCredential: true,
        placeholder: 'Your Client Secret' },
      { key: 'region', label: 'Region', type: 'select', required: true, options: [
        { value: 'com', label: 'US (zoho.com)' },
        { value: 'eu', label: 'Europe (zoho.eu)' },
        { value: 'in', label: 'India (zoho.in)' },
        { value: 'com.au', label: 'Australia (zoho.com.au)' },
        { value: 'com.cn', label: 'China (zoho.com.cn)' }
      ], help: 'Select your Zoho CRM region' }
    ]
  },
  zoho_creator: {
    id: 'zoho_creator',
    name: 'Zoho Creator',
    icon: 'LayoutGrid',
    color: '#1b8755',
    description: 'Connect to Zoho Creator to access custom apps and data',
    isOAuth: true,
    oauthProvider: 'zoho_creator',
    fields: [
      { key: 'oauth_client_id', label: 'Client ID', type: 'text', required: true, isOAuthCredential: true,
        placeholder: 'From Zoho API Console', help: 'Create at api-console.zoho.com' },
      { key: 'oauth_client_secret', label: 'Client Secret', type: 'password', required: true, isOAuthCredential: true,
        placeholder: 'Your Client Secret' },
      { key: 'application_url', label: 'Application URL', type: 'text', required: true,
        placeholder: 'https://creator.zoho.com/your_account/your_app', help: 'The URL of your Zoho Creator application' },
      { key: 'scopes', label: 'Scopes', type: 'text', required: true,
        placeholder: 'ZohoCreator.report.READ,ZohoCreator.form.CREATE',
        help: 'Comma-separated OAuth scopes (e.g. ZohoCreator.report.READ, ZohoCreator.form.CREATE, ZohoCreator.report.UPDATE)' },
      { key: 'region', label: 'Region', type: 'select', required: true, options: [
        { value: 'com', label: 'US (zoho.com)' },
        { value: 'eu', label: 'Europe (zoho.eu)' },
        { value: 'in', label: 'India (zoho.in)' },
        { value: 'com.au', label: 'Australia (zoho.com.au)' },
        { value: 'com.cn', label: 'China (zoho.com.cn)' }
      ], help: 'Select your Zoho Creator region' }
    ]
  }
};

/**
 * Get list of connection types for UI rendering
 */
export const getConnectionTypesForUI = () => {
  return Object.values(CONNECTION_TYPES);
};

/**
 * Get all connection types
 */
export const getAllConnectionTypes = () => {
  return Object.values(CONNECTION_TYPES);
};
